/**
 * 请求日志详情弹窗（Alice 的 #modal-log-detail）：元信息两列网格，加请求与响应正文。
 *
 * 背景：列表接口不返回正文，点「详情」时才按请求号取 /control/admin/logs/{id}/detail。
 *   本仓的正文一律保存（合规备查），按全局保存天数到期删除，所以详情要说明为什么没有正文，
 *   读到正文时后端会写一条审计，弹窗里提示这一点。
 * 设计意图：元信息字段对照 Alice 的 metaFields，按本仓的字段取舍（账号代替用户 ID，没有 IP 归属地）；
 *   正文用 pre 展示，JSON 缩进；导出 JSON 是前端把当前详情存成文件，不再请求后端。
 * 关键约束：弹窗关闭后迟到的响应要丢弃（用请求序号判断），不能把上一条的内容写进下一条。
 */
import { useEffect, useRef, useState } from 'react'
import type { LogDetail } from '@ap/api-client/admin-runtime'
import { runtimeApi, saveBlob } from '@ap/api-client/admin-runtime'
import { AG_runtime as G, errorTextRuntime } from '@ap/i18n/admin-runtime'
import { useI18n } from '@ap/i18n'
import { fmtDateTime } from '@ap/ui'
import { AliceModal, fmtInt, useMoney } from '../../../alice/kit-runtime.tsx'

const pre = {
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border-medium)',
  borderRadius: 6,
  padding: 12,
  fontSize: 12,
  fontFamily: 'var(--mono)',
  overflow: 'auto',
  maxHeight: 220,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-all',
  color: 'var(--text-primary)',
  margin: 0,
} as const

const heading = {
  fontSize: 11,
  fontWeight: 600,
  letterSpacing: '.06em',
  textTransform: 'uppercase',
  color: 'var(--text-tertiary)',
  marginBottom: 6,
} as const

function pretty(v: unknown): string {
  if (v === null || v === undefined || v === '') return ''
  if (typeof v === 'string') return v
  return JSON.stringify(v, null, 2)
}

export function LogDetailModal({ requestId, onClose }: { requestId: string | null; onClose: () => void }) {
  const { t, locale } = useI18n()
  const money = useMoney()
  const [data, setData] = useState<LogDetail | null>(null)
  const [error, setError] = useState('')
  const seq = useRef(0)

  useEffect(() => {
    if (!requestId) return
    const my = ++seq.current
    setData(null)
    setError('')
    runtimeApi
      .logDetail(requestId)
      .then((d) => {
        if (my === seq.current) setData(d)
      })
      .catch((e) => {
        console.error('[logs] 详情加载失败', requestId, e)
        if (my === seq.current) setError(errorTextRuntime(e, locale))
      })
  }, [requestId, locale])

  const d = data
  const dash = '—'
  const fields: [string, string][] = d
    ? [
        [t(G.detail.fRequestId), d.request_id],
        [t(G.detail.fAccountName), d.account_name || dash],
        [t(G.detail.fAccountId), d.account_id],
        [t(G.detail.fKey), d.api_key_id || dash],
        [t(G.detail.fEndUser), d.end_user || dash],
        [t(G.detail.fChannel), d.channel_id || dash],
        [t(G.detail.fModel), d.shell_display_name ? `${d.model} (${d.shell_display_name})` : d.model],
        [t(G.detail.fUpstreamModel), d.upstream_model || dash],
        [t(G.detail.fEndpoint), d.endpoint || dash],
        [t(G.detail.fSource), d.source || dash],
        [t(G.detail.fPurpose), d.purpose || dash],
        [t(G.detail.fPriority), d.priority || dash],
        [t(G.detail.fStream), d.stream ? t(G.common.yes) : t(G.common.no)],
        [t(G.detail.fStatus), d.status],
        [t(G.detail.fStatusCode), String(d.status_code)],
        ...(d.error_code ? ([[t(G.detail.fErrorCode), d.error_code]] as [string, string][]) : []),
        [t(G.detail.fDuration), d.duration_ms === null ? dash : `${d.duration_ms} ms`],
        [t(G.detail.fTtft), d.ttft_ms === null ? dash : `${d.ttft_ms} ms`],
        [t(G.detail.fQueueWait), `${d.queue_wait_ms} ms`],
        [t(G.detail.fAttempts), String(d.attempts)],
        [t(G.detail.fInput), fmtInt(d.input_tokens)],
        [t(G.detail.fOutput), fmtInt(d.output_tokens)],
        [t(G.detail.fCached), fmtInt(d.cached_tokens)],
        [t(G.detail.fCacheWrite), fmtInt(d.cache_creation_tokens)],
        ...(d.images > 0 ? ([[t(G.detail.fImages), fmtInt(d.images)]] as [string, string][]) : []),
        ...(d.list_cost !== null && d.list_cost > d.cost
          ? ([
              [t(G.detail.fListPrice), `${money.petals(d.list_cost)} (${money.usd(d.list_cost, 8)})`],
              [t(G.detail.fDiscount), `${money.petals(d.list_cost - d.cost)} (${money.usd(d.list_cost - d.cost, 8)})`],
              [t(G.detail.fDiscountWhy), d.discount_code || t(G.logs.subsidy)],
            ] as [string, string][])
          : []),
        [t(G.detail.fCharge), `${money.petals(d.cost)} (${money.usd(d.cost, 8)})`],
        [t(G.detail.fUpstream), d.upstream_cost === null ? dash : money.usd(d.upstream_cost, 8)],
        [t(G.detail.fBasis), d.upstream_cost_basis || dash],
        [t(G.detail.fBilling), d.billing_mode || dash],
        [t(G.detail.fTime), fmtDateTime(d.created_at, locale) || dash],
        [t(G.detail.fIp), d.client_ip || dash],
        [t(G.detail.fDevice), d.device_id || dash],
        [t(G.detail.fVersion), d.app_version || dash],
      ]
    : []

  const retained = d?.body.state === 'retained'
  const body = (v: unknown, isResponse: boolean) => {
    if (error) return t(G.detail.failed, { msg: error })
    if (!d) return t(G.detail.loading)
    if (!retained) return t(G.detail.notRetained)
    const text = pretty(v) || t(G.detail.none)
    return isResponse && d.error_message ? `${t(G.detail.errorPrefix, { msg: d.error_message })}\n\n${text}` : text
  }

  return (
    <AliceModal open={requestId !== null} onClose={onClose} title={t(G.detail.title)} width={720} dismissOnMask>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', border: '1px solid var(--border-medium)', borderRadius: 8, overflow: 'hidden', marginBottom: 16, fontSize: 13 }}>
        {fields.map(([k, v], i) => (
          <div key={k} style={{ padding: '8px 14px', borderBottom: '1px solid var(--border)', ...(i % 2 === 0 ? { borderRight: '1px solid var(--border)', background: 'var(--bg-subtle)' } : {}) }}>
            <div style={{ fontSize: 10, fontWeight: 600, color: 'var(--text-tertiary)', textTransform: 'uppercase', letterSpacing: '.05em', marginBottom: 2 }}>{k}</div>
            <div style={{ fontSize: 13, fontFamily: 'var(--mono)', wordBreak: 'break-all' }}>{v}</div>
          </div>
        ))}
      </div>
      {d && retained && d.body.expires_at && (
        <div style={{ fontSize: 11, color: 'var(--text-tertiary)', marginBottom: 10 }}>{t(G.detail.retainedUntil, { t: fmtDateTime(d.body.expires_at, locale) })}</div>
      )}
      <div style={{ marginBottom: 12 }}>
        <div style={heading}>{t(G.detail.requestBody)}</div>
        <pre style={pre}>{body(d?.body.request, false)}</pre>
      </div>
      <div>
        <div style={heading}>{t(G.detail.responseBody)}</div>
        <pre style={pre}>{body(d?.body.response, true)}</pre>
      </div>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
        <button
          type="button"
          className="btn btn-secondary btn-sm"
          disabled={!d}
          onClick={() => d && saveBlob(new Blob([`${JSON.stringify(d, null, 2)}\n`], { type: 'application/json' }), `request-log-${d.request_id}.json`)}
        >
          {t(G.common.exportJson)}
        </button>
      </div>
    </AliceModal>
  )
}
